import React, { Component, ReactNode } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
} from '@mui/material';
import {
  ErrorOutline as ErrorIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
/**
 * Error information passed to error callbacks and fallback components
 */
export interface ErrorInfo {
  error: Error;
  errorInfo: React.ErrorInfo;
  resetError: () => void;
}

/**
 * Props for ErrorBoundary component
 */
export interface ErrorBoundaryProps {
  /**
   * Child components to wrap with error boundary
   */
  children: ReactNode;

  /**
   * Optional custom fallback UI renderer
   * If not provided, uses default Material-UI fallback
   * 
   * @param error - The error that was caught
   * @param errorInfo - React error info with component stack
   * @param resetError - Function to reset error state and retry rendering
   */
  fallback?: (errorInfo: ErrorInfo) => ReactNode;

  /**
   * Optional callback when an error is caught
   * Use this to log errors to external services
   * 
   * @param error - The error that was caught
   * @param errorInfo - React error info with component stack
   */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;

  /**
   * Optional callback when error state is reset
   * Useful for cleanup or analytics
   */
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * ErrorBoundary Component
 * 
 * Catches JavaScript errors anywhere in the child component tree and displays
 * a fallback UI instead of crashing the entire application.
 * 
 * Features:
 * - Catches rendering errors, lifecycle errors, and constructor errors
 * - Displays user-friendly error message with recovery options
 * - Customizable fallback UI via props
 * - Optional error logging callback
 * - Reset functionality to retry rendering
 * 
 * Usage:
 * ```tsx
 * // Basic usage with default UI
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 * 
 * // With error logging
 * <ErrorBoundary onError={(error, info) => logToService(error, info)}>
 *   <App />
 * </ErrorBoundary>
 * 
 * // With custom fallback UI
 * <ErrorBoundary fallback={({ error, resetError }) => (
 *   <CustomErrorUI error={error} onRetry={resetError} />
 * )}>
 *   <App />
 * </ErrorBoundary>
 * ```
 * 
 * @see https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 */
function DefaultErrorFallback({ onRetry, onGoHome }: { onRetry: () => void; onGoHome: () => void }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 3,
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
          }}
        >
          <ErrorIcon
            color="error"
            sx={{
              fontSize: 80,
              mb: 2,
            }}
          />

          <Typography variant="h4" component="h1" gutterBottom color="error">
            Something went wrong
          </Typography>

          <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 3 }}>
            An unexpected error occurred. Please try again or return to the home page.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={onRetry}
              size="large"
            >
              Try Again
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<HomeIcon />}
              onClick={onGoHome}
              size="large"
            >
              Go Home
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
            If the problem persists, please contact support.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  /**
   * React lifecycle method called when an error is caught
   * Updates state to trigger fallback UI rendering
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * React lifecycle method called after an error is caught
   * Used for side effects like error logging
   */
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Store error info in state for display
    this.setState({ errorInfo });

    // Call optional error callback for logging/analytics
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  /**
   * Reset error state and attempt to re-render children
   * Called when user clicks "Try Again" button
   */
  resetError = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Call optional reset callback
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  /**
   * Navigate to home page (full page reload)
   */
  goHome = (): void => {
    window.location.href = '/';
  };

  /**
   * Render default fallback UI with Material-UI components
   */
  renderDefaultFallback(): ReactNode {
    return <DefaultErrorFallback onRetry={this.resetError} onGoHome={this.goHome} />;
  }

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback } = this.props;

    if (!hasError) {
      return children;
    }

    // If errorInfo isn't available yet, show default fallback to prevent blank screen
    if (!error || !errorInfo) {
      return this.renderDefaultFallback();
    }

    // If custom fallback provided, use it with complete error information
    if (fallback) {
      return fallback({
        error,
        errorInfo,
        resetError: this.resetError,
      });
    }

    // Otherwise render default fallback UI
    return this.renderDefaultFallback();
  }
}

